import { useState } from 'react';
import { useIonRouter } from '@ionic/react';
import type { PostCardFieldsFragment } from '../../../../types/graphql-generated';
import { MarkdownContent } from '../../../../components/markdown/MarkdownContent';
import { PostMeta } from './PostMeta';
import { PostLinkAttachment } from './PostLinkAttachment';
import { PostPhotoGrid } from './photos/PostPhotoGrid';
import { ChannelImage } from './ChannelImage';
import { truncatePostText } from '../utils/postDate';

interface PostCardProps {
  post: PostCardFieldsFragment;
}

// A compact preview row for a single post in the channels timeline. Twitter-style
// hairline rows (no card) with the channel's image on the left. The author's
// name is only shown when the channel publishes it (`publishAuthorName`). Tapping the row
// opens the full-post detail screen. When the text is long, the "(Mostrar más)"
// link expands it in place without leaving the feed.
export const PostCard = ({ post }: PostCardProps) => {
  const ionRouter = useIonRouter();
  const { preview, isTruncated } = truncatePostText(post.text);
  const [expanded, setExpanded] = useState(false);

  const openPost = () => {
    ionRouter.push(`/app/main/channels/post/${post.id}`);
  };

  // Toggle the inline expansion. Stop propagation so tapping the link does not
  // also trigger the row's navigation to the detail screen.
  const toggleExpanded = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setExpanded((value) => !value);
  };

  const body = isTruncated && !expanded ? preview : post.text.trim();

  return (
    <div
      className="flex cursor-pointer gap-3 border-b border-[#e4e8f0] py-3.5 last:border-b-0"
      onClick={openPost}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openPost();
        }
      }}
    >
      <ChannelImage
        image={post.channel?.image}
        updatedAt={post.channel?.updatedAt}
        name={post.channel?.name}
        size={38}
      />
      <div className="min-w-0 flex-1">
        <PostMeta
          channelName={post.channel?.name ?? ''}
          channelId={post.channel?.id}
          authorName={post.channel?.publishAuthorName ? post.author?.fullName : null}
          publishedAt={post.publishedAt}
          editedAt={post.editedAt}
        />
        <MarkdownContent content={body} className="mt-1.5 text-[14px] text-[#2a3242]" />
        {isTruncated && (
          <span
            role="button"
            tabIndex={0}
            className="text-primary text-sm font-medium cursor-pointer hover:underline"
            onClick={toggleExpanded}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                toggleExpanded(event);
              }
            }}
          >
            {expanded ? '(Mostrar menos)' : '(Mostrar más)'}
          </span>
        )}
        <PostLinkAttachment url={post.linkUrl} title={post.linkTitle} />
        <PostPhotoGrid photos={post.photos} />
      </div>
    </div>
  );
};
